/**
 * Canvas-based hex map renderer.
 * Optimized for performance with:
 * - Viewport culling (only render visible hexes)
 * - requestAnimationFrame for smooth rendering
 * - Memoized drawing functions
 */

import { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import type { HexData, Viewport } from './types';
import { TERRAIN_COLORS } from './types';
import { getTileUrl } from './tiles';
import {
    axialToPixel,
    getHexCorners,
    pixelToAxial,
    hexRound,
    hexKey,
    HEX_SIZE,
    type AxialCoord,
} from '@/lib/hexMath';

interface Props {
    hexes: HexData[];
    viewport: Viewport;
    width: number;
    height: number;
    onHexClick?: (hex: HexData | null, coord: AxialCoord) => void;
    /** Called when mouse moves over a hex (or leaves the canvas) - desktop only */
    onHexHover?: (hex: HexData | null, coord: AxialCoord | null, mousePos: { x: number; y: number } | null) => void;
    /** Called when user taps on a hex on mobile - handles two-tap interaction */
    onMobileTap?: (hex: HexData | null, coord: AxialCoord, screenPos: { x: number; y: number }) => void;
    getHexAt: (q: number, r: number) => HexData | null;
    /** Set of hex IDs that were recently updated (for visual highlighting) */
    recentlyUpdatedIds?: Set<number>;
    /** Set of coordinate keys (e.g., "1,2") for unclaimed hexes that are claimable */
    claimableCoords?: Set<string>;
    /** Whether all unclaimed hexes are claimable (GMs can claim anywhere) */
    allUnclaimedClaimable?: boolean;
    /** Background theme - controls whether the canvas paints its own background */
    backgroundTheme?: 'current' | 'parchment' | 'starfield';
}

// Color for unclaimed hexes (shown as outline only)
const UNCLAIMED_COLOR = '#374151';
const BACKGROUND_COLOR = '#1e293b';
// Highlight color for recently updated hexes (golden glow)
const HIGHLIGHT_COLOR = '#FFD700';
const HIGHLIGHT_LINE_WIDTH = 3;
// Color for claimable hexes (green glow to indicate they can be claimed)
const CLAIMABLE_COLOR = '#22c55e';
const CLAIMABLE_FILL_COLOR = 'rgba(34, 197, 94, 0.15)';
// Hover effect styling (subtle, accessible)
const HOVER_STROKE_COLOR = 'rgba(255, 255, 255, 0.95)';
const HOVER_SHADOW_COLOR = 'rgba(255, 255, 255, 0.8)';
const HOVER_FILL_OVERLAY = 'rgba(255, 255, 255, 0.08)';

export function HexMapCanvas({
    hexes,
    viewport,
    width,
    height,
    onHexClick,
    onHexHover,
    onMobileTap,
    getHexAt,
    recentlyUpdatedIds,
    claimableCoords,
    allUnclaimedClaimable = false,
    backgroundTheme = 'current',
}: Props) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    // Track touch start position to detect if it was a tap vs drag
    const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
    // Threshold for detecting a tap vs drag (in pixels)
    const TAP_THRESHOLD = 10;
    // Track currently hovered hex coordinate (desktop only)
    const [hovered, setHovered] = useState<AxialCoord | null>(null);

    // Draw a single hex
    // Cache images by URL to avoid reloading
    const imageCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

    const drawHex = useCallback(
        (ctx: CanvasRenderingContext2D, hex: HexData, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Resolve image URL: prefer tileImage, then defaultImage (domain-aware or absolute)
            const resolveUrl = () => {
                if (hex.domain && hex.tileImage) {
                    return getTileUrl(hex.domain as any, hex.tileImage);
                }
                if (hex.domain && hex.defaultImage) {
                    return getTileUrl(hex.domain as any, hex.defaultImage);
                }
                if (hex.defaultImage && (hex.defaultImage.startsWith('http') || hex.defaultImage.startsWith('/'))) {
                    return hex.defaultImage;
                }
                return null;
            };

            const url = resolveUrl();
            if (url) {
                let img = imageCacheRef.current.get(url);
                if (!img) {
                    img = new Image();
                    img.src = url;
                    imageCacheRef.current.set(url, img);
                }

                if (img.complete) {
                    ctx.save();
                    ctx.clip();
                    // Compute bounding box for the hex
                    const xs = corners.map(c => c.x);
                    const ys = corners.map(c => c.y);
                    const minX = Math.min(...xs);
                    const maxX = Math.max(...xs);
                    const minY = Math.min(...ys);
                    const maxY = Math.max(...ys);
                    const w = maxX - minX;
                    const h = maxY - minY;
                    // Cover the hex with the image
                    ctx.drawImage(img, minX, minY, w, h);
                    ctx.restore();
                } else {
                    // Fallback fill while image loads
                    ctx.fillStyle = TERRAIN_COLORS[hex.terrain];
                    ctx.fill();
                }
            } else {
                // Fallback: terrain color
                ctx.fillStyle = TERRAIN_COLORS[hex.terrain];
                ctx.fill();
            }

            // Draw border
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 1;
            ctx.stroke();
        },
        [viewport.zoom]
    );

    // Draw an unclaimed hex outline
    const drawUnclaimedHex = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            ctx.strokeStyle = UNCLAIMED_COLOR;
            ctx.lineWidth = 0.5;
            ctx.stroke();
        },
        [viewport.zoom]
    );

    // Draw a highlight glow around a hex (for recently updated hexes)
    const drawHighlight = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Draw golden glow border
            ctx.strokeStyle = HIGHLIGHT_COLOR;
            ctx.lineWidth = HIGHLIGHT_LINE_WIDTH;
            ctx.shadowColor = HIGHLIGHT_COLOR;
            ctx.shadowBlur = 10;
            ctx.stroke();

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        },
        [viewport.zoom]
    );

    // Draw a claimable hex (unclaimed but adjacent to claimed hex)
    const drawClaimableHex = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Fill with semi-transparent green
            ctx.fillStyle = CLAIMABLE_FILL_COLOR;
            ctx.fill();

            // Draw green border with subtle glow
            ctx.strokeStyle = CLAIMABLE_COLOR;
            ctx.lineWidth = 2;
            ctx.shadowColor = CLAIMABLE_COLOR;
            ctx.shadowBlur = 6;
            ctx.stroke();

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        },
        [viewport.zoom]
    );

    // Draw hover effect (works for both claimed and unclaimed hexes)
    const drawHover = useCallback(
        (ctx: CanvasRenderingContext2D, centerX: number, centerY: number) => {
            const size = HEX_SIZE * viewport.zoom;
            const corners = getHexCorners({ x: centerX, y: centerY }, size);

            ctx.beginPath();
            ctx.moveTo(corners[0].x, corners[0].y);
            for (let i = 1; i < 6; i++) {
                ctx.lineTo(corners[i].x, corners[i].y);
            }
            ctx.closePath();

            // Subtle inner fill to increase perceived brightness without distraction
            ctx.fillStyle = HOVER_FILL_OVERLAY;
            ctx.fill();

            // Bright border with soft glow
            ctx.strokeStyle = HOVER_STROKE_COLOR;
            ctx.lineWidth = 2;
            ctx.shadowColor = HOVER_SHADOW_COLOR;
            ctx.shadowBlur = 8;
            ctx.stroke();

            // Reset shadow
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
        },
        [viewport.zoom]
    );

    // Memoize visible hexes to avoid filtering on every render
    const visibleHexes = useMemo(() => {
        // Calculate the visible area in hex coordinates
        const centerPixelX = width / 2 + viewport.offsetX;
        const centerPixelY = height / 2 + viewport.offsetY;
        const scaledSize = HEX_SIZE * viewport.zoom;

        // Filter hexes that are within the visible viewport (with padding)
        const padding = scaledSize * 2;
        return hexes.filter((hex) => {
            const pixel = axialToPixel(hex, scaledSize);
            const screenX = centerPixelX + pixel.x;
            const screenY = centerPixelY + pixel.y;
            return (
                screenX >= -padding &&
                screenX <= width + padding &&
                screenY >= -padding &&
                screenY <= height + padding
            );
        });
    }, [hexes, viewport, width, height]);

    // Main render function using requestAnimationFrame for smooth rendering
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Use requestAnimationFrame for smoother rendering
        const animationId = requestAnimationFrame(() => {
            // Clear canvas / background
            if (backgroundTheme === 'current') {
                // Existing solid background color
                ctx.fillStyle = BACKGROUND_COLOR;
                ctx.fillRect(0, 0, width, height);
            } else {
                // Transparent, let parent background show through
                ctx.clearRect(0, 0, width, height);
            }

            // Calculate visible range
            const centerX = width / 2 + viewport.offsetX;
            const centerY = height / 2 + viewport.offsetY;

            // Determine visible hex range based on viewport
            const visibleRadius = Math.ceil(Math.max(width, height) / (HEX_SIZE * viewport.zoom * 1.5)) + 2;

            // Draw unclaimed hex grid first (background)
            // Also track which unclaimed hexes are claimable for highlighting
            for (let q = -visibleRadius; q <= visibleRadius; q++) {
                for (let r = -visibleRadius; r <= visibleRadius; r++) {
                    if (!getHexAt(q, r)) {
                        const pixel = axialToPixel({ q, r }, HEX_SIZE * viewport.zoom);
                        const key = hexKey({ q, r });

                        // Check if this unclaimed hex is claimable (adjacent to a claimed hex)
                        // Note: GMs can claim anywhere but we don't highlight all hexes green for them
                        if (!allUnclaimedClaimable && claimableCoords && claimableCoords.has(key)) {
                            drawClaimableHex(ctx, centerX + pixel.x, centerY + pixel.y);
                        } else {
                            drawUnclaimedHex(ctx, centerX + pixel.x, centerY + pixel.y);
                        }
                    }
                }
            }

            // Draw only visible claimed hexes on top
            visibleHexes.forEach((hex) => {
                const pixel = axialToPixel(hex, HEX_SIZE * viewport.zoom);
                drawHex(ctx, hex, centerX + pixel.x, centerY + pixel.y);
            });

            // Draw highlights for recently updated hexes (on top of everything)
            if (recentlyUpdatedIds && recentlyUpdatedIds.size > 0) {
                visibleHexes.forEach((hex) => {
                    if (recentlyUpdatedIds.has(hex.id)) {
                        const pixel = axialToPixel(hex, HEX_SIZE * viewport.zoom);
                        drawHighlight(ctx, centerX + pixel.x, centerY + pixel.y);
                    }
                });
            }

            // Hovered hex overlay (draw last so it is clearly visible)
            if (hovered) {
                const pixel = axialToPixel(hovered, HEX_SIZE * viewport.zoom);
                drawHover(ctx, centerX + pixel.x, centerY + pixel.y);
            }
        });

        // Cleanup animation frame on unmount or re-render
        return () => cancelAnimationFrame(animationId);
    }, [visibleHexes, viewport, width, height, drawHex, drawUnclaimedHex, drawHighlight, drawClaimableHex, drawHover, getHexAt, recentlyUpdatedIds, claimableCoords, allUnclaimedClaimable, hovered, backgroundTheme]);

    // Handle click
    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            if (!onHexClick) return;

            const canvas = canvasRef.current;
            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Convert to hex coordinates
            const centerX = width / 2 + viewport.offsetX;
            const centerY = height / 2 + viewport.offsetY;
            const relX = x - centerX;
            const relY = y - centerY;

            const fractionalHex = pixelToAxial({ x: relX, y: relY }, HEX_SIZE * viewport.zoom);
            const coord = hexRound(fractionalHex);
            const hex = getHexAt(coord.q, coord.r);

            onHexClick(hex, coord);
        },
        [onHexClick, viewport, width, height, getHexAt]
    );

    // Handle mouse move for hover tooltip
    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;

            // Convert to hex coordinates
            const centerX = width / 2 + viewport.offsetX;
            const centerY = height / 2 + viewport.offsetY;
            const relX = x - centerX;
            const relY = y - centerY;

            const fractionalHex = pixelToAxial({ x: relX, y: relY }, HEX_SIZE * viewport.zoom);
            const coord = hexRound(fractionalHex);
            const hex = getHexAt(coord.q, coord.r);

            // Track hovered coordinate for visual overlay
            setHovered(coord);

            // Pass screen position for tooltip positioning
            if (onHexHover) {
                onHexHover(hex, coord, { x: e.clientX, y: e.clientY });
            }
        },
        [onHexHover, viewport, width, height, getHexAt]
    );

    // Handle mouse leave - hide tooltip
    const handleMouseLeave = useCallback(() => {
        if (onHexHover) {
            onHexHover(null, null, null);
        }
        // Clear hover state when leaving canvas
        setHovered(null);
    }, [onHexHover]);

    // Handle touch start - record position for tap detection
    const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            touchStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
            };
        }
    }, []);

    // Handle touch end - detect tap vs drag and trigger appropriate handler
    const handleTouchEnd = useCallback(
        (e: React.TouchEvent<HTMLCanvasElement>) => {
            if (!onMobileTap || !touchStartRef.current) return;

            const touch = e.changedTouches[0];
            const dx = touch.clientX - touchStartRef.current.x;
            const dy = touch.clientY - touchStartRef.current.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            // Only treat as tap if movement was minimal (not a drag)
            if (distance < TAP_THRESHOLD) {
                const canvas = canvasRef.current;
                if (!canvas) return;

                const rect = canvas.getBoundingClientRect();
                const x = touch.clientX - rect.left;
                const y = touch.clientY - rect.top;

                // Convert to hex coordinates
                const centerX = width / 2 + viewport.offsetX;
                const centerY = height / 2 + viewport.offsetY;
                const relX = x - centerX;
                const relY = y - centerY;

                const fractionalHex = pixelToAxial({ x: relX, y: relY }, HEX_SIZE * viewport.zoom);
                const coord = hexRound(fractionalHex);
                const hex = getHexAt(coord.q, coord.r);

                // Call mobile tap handler with screen position for tooltip
                onMobileTap(hex, coord, { x: touch.clientX, y: touch.clientY });
            }

            touchStartRef.current = null;
        },
        [onMobileTap, viewport, width, height, getHexAt]
    );

    return (
        <canvas
            ref={canvasRef}
            width={width}
            height={height}
            onClick={handleClick}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="relative cursor-grab active:cursor-grabbing touch-none"
        />
    );
}
